import { wsClient } from './websocket';
import { useAuthStore } from '../store/authStore';
import { t } from '../i18n';
import type {
  WSMessageDeliverData,
  WSMessageStatusUpdateData,
  WSMessageEditData,
  WSMessageDeleteData,
  WSMessageMutationUpdateData,
  WSMessageSyncResultData,
  WSMessageSyncStatusResultData,
  WSGroupChatUpdatedData,
  WSGroupMessageDeliverData,
  WSGroupMessageUpdatedData,
  WSMessageStatusData,
  WSMessageSyncAckData,
  WSMessageSyncStatusAckData,
  WSMessageAckData,
  PublicKey,
  WSMarkReadData,
  WSReadCursorPushData,
  DirectMessageReplyReference,
  IdentityId,
} from '@shared/types';
import { deriveIdentityIdFromPublicKey } from '@shared/identityId';
import {
  buildThreadKey,
  getThreadMaxChatSeq,
  saveMessages,
  updateMessageStatus,
  markThreadReadUpTo,
  deleteMessage,
  getMessageById,
  type StoredMessage
} from '../messages/storage';
import type { LocalHistoryCiphertext } from '../historySecrets/types';
import { signPayloadWithKey, signPlaintextWithKey, verifyPlaintext } from '../crypto/messageSigning';
import { decryptMessageAsyncWithKey } from '../crypto/messageEncryption';
import { decryptDirectEpochMessage, isDirectEpochCiphertext } from '../directChats/crypto';
import { ensureDirectEpochKey } from '../directChats/keys';
import { showToast } from '../utils/toast';
import { useVaultStore } from '../store/vaultStore';
import { finalizePagedSync } from './pagedSync';
import { APIError } from '../api/client';
import { directMessagesApi } from '../api/directMessages';
import {
  resolveStrictHttpMessageActionContext,
  resolveStrictMessageActionContext,
  resolveStrictMessagingLocalContext,
  type MessageActionContext,
  type MessagingLocalContext
} from './actionContextResolver';
import { findTemporaryContactByIdentityId } from '../utils/temporaryContacts';
import { useCallHistoryStore } from '../store/callHistoryStore';
import { logChatCrypto } from '../utils/chatCryptoDebug';
import { refreshPresenceForIdentity } from './presence';
import { hasCircleServerAccessRemoved } from '../utils/removedCircleAccess';

const MAX_MESSAGE_BYTES = Number(import.meta.env.VITE_MAX_MESSAGE_BYTES || '16384');
const ACK_TIMEOUT_MS = 30_000;
const RETRY_WINDOW_MS = 23 * 60 * 60 * 1000;
const CONTEXT_TIMEOUT_MS = 10_000;
const FOCUSED_SYNC_PAGE_SIZE = 500;
const focusedDirectThreadSyncInFlight = new Map<string, Promise<void>>();

// Fix 4b: unconditional sync triggers (defense in depth, independent of live events).
const UNCONDITIONAL_SYNC_INTERVAL_MS = 45_000;
const RECONNECT_SYNC_MAX_JITTER_MS = 4_000;
// Last opened direct thread, re-synced on reconnect / visibility / timer so a
// gap is closed even when no live event arrives.
let lastFocusedThread: { localIdentityId: string; peerIdentityId: string } | null = null;
let unconditionalSyncTimer: ReturnType<typeof setInterval> | null = null;

function getErrorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error && typeof (error as { code?: unknown }).code === 'string') {
    return String((error as { code: string }).code).trim();
  }
  if (error instanceof Error) {
    return String(error.message || '').trim();
  }
  return typeof error === 'string' ? error.trim() : '';
}

function humanizeMessageTransportError(error: unknown): string {
  switch (getErrorCode(error)) {
    case 'WS_NOT_READY_FOR_SERVER':
      return t('messages.errors.wsReconnectPending');
    case 'SERVER_CONTEXT_SWITCH_TIMEOUT':
      return t('messages.errors.serverSwitchTimeout');
    case 'TEMPORARY_SERVER_URL_MISSING':
      return t('messages.errors.serverUnavailable');
    case 'NO_LOCAL_IDENTITY_CONTEXT':
    case 'MISSING_LOCAL_IDENTITY':
    case 'EXPLICIT_LOCAL_IDENTITY_NOT_FOUND':
    case 'AMBIGUOUS_LOCAL_IDENTITY_FOR_MESSAGE':
    case 'LOCAL_IDENTITY_NOT_FOUND_FOR_TARGET_SERVER':
      return t('messages.errors.localIdentityUnavailable');
    default:
      return t('common.unknownError');
  }
}

function isKnownTransportError(error: unknown): boolean {
  const code = getErrorCode(error);
  return [
    'WS_NOT_READY_FOR_SERVER',
    'SERVER_CONTEXT_SWITCH_TIMEOUT',
    'TEMPORARY_SERVER_URL_MISSING',
    'NO_LOCAL_IDENTITY_CONTEXT',
    'MISSING_LOCAL_IDENTITY',
    'EXPLICIT_LOCAL_IDENTITY_NOT_FOUND',
    'AMBIGUOUS_LOCAL_IDENTITY_FOR_MESSAGE',
    'LOCAL_IDENTITY_NOT_FOUND_FOR_TARGET_SERVER'
  ].includes(code);
}

function humanizeMessageMutationError(error: unknown, action: 'edit' | 'delete'): string {
  if (isKnownTransportError(error)) {
    return humanizeMessageTransportError(error);
  }
  if (error instanceof APIError) {
    if (error.status === 0 || error.status >= 500) {
      return t(action === 'edit'
        ? 'messages.errors.editServerUnavailable'
        : 'messages.errors.deleteServerUnavailable');
    }
    if (error.status === 401 || error.status === 403) {
      return t('messages.errors.mutationAccessDenied');
    }
    if (error.status === 404) {
      return t('messages.errors.messageNoLongerAvailable');
    }
  }
  return t(action === 'edit' ? 'messages.errors.editFailed' : 'messages.errors.deleteFailed');
}

function humanizeMessageRetryError(error: unknown): string {
  if (isKnownTransportError(error)) {
    return humanizeMessageTransportError(error);
  }
  if (error instanceof APIError) {
    if (error.status === 0 || error.status >= 500) {
      return t('messages.errors.retryServerUnavailable');
    }
    if (error.status === 401 || error.status === 403) {
      return t('messages.errors.mutationAccessDenied');
    }
  }
  return t('messages.errors.retryFailed');
}

let initialized = false;
const pendingOutbound = new Map<string, {
  message: StoredMessage;
  timeoutId?: number;
  afterAck?: (ack: WSMessageAckData) => Promise<void> | void;
  resolveAck?: () => void;
  rejectAck?: (error: unknown) => void;
}>();

function toStoredMessages(messages: WSMessageDeliverData[], currentIdentityId: string): StoredMessage[] {
  return messages.map((message) => ({
    serverMessageId: message.serverMessageId,
    senderIdentityId: message.senderIdentityId,
    recipientIdentityId: message.recipientIdentityId,
    senderIdentityPublicKey: message.senderIdentityPublicKey,
    senderIdentityName: message.senderIdentityName,
    ciphertext: message.ciphertext,
    senderCiphertext: message.senderCiphertext,
    senderSignature: message.senderSignature,
    epoch: message.epoch,
    serverTimestamp: Number(message.serverTimestamp),
    editedAt: message.editedAt ?? null,
    deletedAt: message.deletedAt ?? null,
    contentUpdatedAt: message.contentUpdatedAt ?? Number(message.serverTimestamp),
    revision: message.revision ?? 1,
    chatSeq: typeof message.chatSeq === 'number' ? message.chatSeq : undefined,
    replyTo: message.replyTo ?? null,
    localHistoryCiphertext: message.deletedAt ? null : undefined,
    status: message.senderIdentityId === currentIdentityId ? 'new' : 'delivered',
    direction: message.senderIdentityId === currentIdentityId ? 'outbound' : 'inbound'
  }));
}

function resolveInboundVerificationKey(data: WSMessageDeliverData): {
  key: PublicKey | null;
  source: 'local' | 'server' | 'missing';
  contactId?: string;
  contactServerId?: string;
  senderName: string;
} {
  const senderId = (data.senderIdentityId || '').trim();
  const vault = useVaultStore.getState();
  const contact = senderId ? vault.findContactByIdentityId(senderId) : null;
  const temporaryContact = contact ? null : findTemporaryContactByIdentityId(useAuthStore.getState().temporaryMemberships, senderId);
  const effectiveContact = contact || temporaryContact;
  const contactIdentity = effectiveContact?.identities.find((identity) => identity.identityId === senderId) || null;
  const localKey = contactIdentity?.identityPublicKey || null;
  const serverKey = data.senderIdentityPublicKey || null;

  const senderName =
    (effectiveContact?.displayName || '').trim()
    || (data.senderIdentityName || '').trim()
    || senderId
    || t('common.unknown');

  if (localKey) {
    // Defense-in-depth: in normal flow identityId is derived from key, so this mismatch
    // should be rare. We still enforce strict equality to catch regressions, stale state,
    // or accidental trust-model drift when protocol/code evolves.
    if (serverKey && serverKey.value && localKey.value !== serverKey.value) {
      return {
        key: null,
        source: 'missing',
        contactId: contact?.contactId,
        contactServerId: contactIdentity?.serverId,
        senderName
      };
    }

    return {
      key: localKey,
      source: 'local',
      contactId: contact?.contactId,
      contactServerId: contactIdentity?.serverId,
      senderName
    };
  }

  if (serverKey?.value) {
    return { key: serverKey, source: 'server', senderName };
  }

  return { key: null, source: 'missing', senderName };
}

function markServerKeySeenIfNeeded(contactId?: string, serverId?: string, identityId?: string) {
  const cleanContactId = (contactId || '').trim();
  const cleanServerId = (serverId || '').trim();
  const cleanIdentityId = (identityId || '').trim();
  if (!cleanContactId || !cleanServerId || !cleanIdentityId) return;

  const vault = useVaultStore.getState();
  const contact = vault.vaultData?.contacts[cleanContactId];
  if (!contact) return;
  const identity = contact.identities.find((item) => item.serverId === cleanServerId && item.identityId === cleanIdentityId);
  if (!identity || identity.serverKeySeenAt) return;

  vault.updateIdentityInContact(cleanContactId, cleanServerId, cleanIdentityId, {
    serverKeySeenAt: new Date().toISOString()
  });
}

function resolveLocalIdentityHintForDeliveredMessage(data: WSMessageDeliverData): string | undefined {
  const state = useAuthStore.getState();
  const sender = (data.senderIdentityId || '').toString().trim();
  const recipient = (data.recipientIdentityId || '').toString().trim();

  if (recipient && state.identities[recipient]) return recipient;
  if (sender && state.identities[sender]) return sender;
  return undefined;
}

async function verifyInboundMessage(data: WSMessageDeliverData, context: MessagingLocalContext): Promise<{
  isValid: boolean;
  senderName: string;
  contactId?: string;
  contactServerId?: string;
}> {
  const senderIdentityId = (data.senderIdentityId || '').trim();
  const signature = (data.senderSignature || '').trim();
  const resolved = resolveInboundVerificationKey(data);
  logChatCrypto('direct', 'verify-inbound-start', {
    senderIdentityId,
    epoch: typeof data.epoch === 'number' ? data.epoch : null,
    keySource: resolved.source,
    hasSignature: Boolean(signature),
    isDirectEpoch: isDirectEpochCiphertext(data.ciphertext)
  });

  if (!senderIdentityId || !signature || !resolved.key?.value) {
    logChatCrypto('direct', 'verify-inbound-missing-prerequisite', {
      senderIdentityId,
      hasSenderIdentityId: Boolean(senderIdentityId),
      hasSignature: Boolean(signature),
      hasResolvedKey: Boolean(resolved.key?.value)
    });
    return {
      isValid: false,
      senderName: resolved.senderName,
      contactId: resolved.contactId,
      contactServerId: resolved.contactServerId
    };
  }

  try {
    const derivedId = await deriveIdentityIdFromPublicKey(resolved.key);
    if ((derivedId || '').trim() !== senderIdentityId) {
      logChatCrypto('direct', 'verify-inbound-derived-id-mismatch', {
        senderIdentityId,
        derivedIdentityId: (derivedId || '').trim()
      });
      return {
        isValid: false,
        senderName: resolved.senderName,
        contactId: resolved.contactId,
        contactServerId: resolved.contactServerId
      };
    }
  } catch {
    logChatCrypto('direct', 'verify-inbound-derived-id-error', { senderIdentityId });
    return {
      isValid: false,
      senderName: resolved.senderName,
      contactId: resolved.contactId,
      contactServerId: resolved.contactServerId
    };
  }

  let plaintext: string | null = null;
  if (isDirectEpochCiphertext(data.ciphertext) && typeof data.epoch === 'number') {
    const directKey = await ensureDirectEpochKey({
      localIdentityId: context.localIdentityId as IdentityId,
      localServerUrl: context.localServerUrl,
      localPublicKey: context.localPublicKey,
      localEcdhKey: context.ecdhKey,
      localEcdhScalar: context.ecdhScalar,
      localSigningKey: context.signingKey,
      peerIdentityId: senderIdentityId as IdentityId,
      peerPublicKey: resolved.key,
      epoch: data.epoch
    });
    logChatCrypto('direct', 'verify-inbound-direct-key', {
      senderIdentityId,
      epoch: data.epoch,
      resolved: Boolean(directKey)
    });
    plaintext = directKey ? await decryptDirectEpochMessage(data.ciphertext, directKey) : null;
  } else {
    plaintext = await decryptMessageAsyncWithKey(
      data.ciphertext,
      resolved.key,
      context.ecdhKey,
      context.ecdhScalar
    );
  }
  logChatCrypto('direct', 'verify-inbound-plaintext', {
    senderIdentityId,
    epoch: typeof data.epoch === 'number' ? data.epoch : null,
    ok: plaintext !== null
  });
  if (plaintext == null) {
    return {
      isValid: false,
      senderName: resolved.senderName,
      contactId: resolved.contactId,
      contactServerId: resolved.contactServerId
    };
  }

  const signatureValid = await verifyPlaintext(plaintext, signature, resolved.key);
  logChatCrypto('direct', 'verify-inbound-signature', {
    senderIdentityId,
    epoch: typeof data.epoch === 'number' ? data.epoch : null,
    ok: signatureValid
  });
  return {
    isValid: signatureValid,
    senderName: resolved.senderName,
    contactId: resolved.contactId,
    contactServerId: resolved.contactServerId
  };
}

async function handleMessageAck(data: WSMessageAckData) {
  const pending = pendingOutbound.get(data.clientMessageId);
  if (pending?.timeoutId) {
    window.clearTimeout(pending.timeoutId);
  }
  pendingOutbound.delete(data.clientMessageId);
  const existing = pending?.message || await getMessageById(data.clientMessageId);
  if (!existing) return;
  await saveMessages([
    {
      ...existing,
      serverMessageId: data.serverMessageId,
      serverTimestamp: Number(data.serverTimestamp),
      status: data.status
    }
  ]);
  await deleteMessage(data.clientMessageId);
  if (pending?.afterAck) {
    try {
      await pending.afterAck(data);
    } catch (error) {
      console.error('Message afterAck hook failed:', error);
      pending.rejectAck?.(error);
      return;
    }
  }
  pending?.resolveAck?.();
}

async function applyMessageStatusUpdate(data: WSMessageStatusUpdateData) {
  await updateMessageStatus(data.serverMessageId, data.status);
}

async function applyMessageReadCursor(data: WSReadCursorPushData) {
  const context = await resolveLocalMessagingContext();
  await markThreadReadUpTo(context.localIdentityId, data.peerIdentityId, data.readThrough);
  void useCallHistoryStore.getState().markMissedCallsSeen({ peerIdentityId: data.peerIdentityId });
}

async function applyMessageMutationUpdate(data: WSMessageMutationUpdateData) {
  const localIdentityIdHint = resolveLocalIdentityHintForDeliveredMessage(data);
  const context = await resolveLocalMessagingContext(localIdentityIdHint);
  const existing = await getMessageById(data.serverMessageId);
  const stored = toStoredMessages([data], context.localIdentityId).map((item) => ({
    ...item,
    status: existing?.status || item.status,
    localHistoryCiphertext: data.deletedAt ? null : item.localHistoryCiphertext,
  }));
  await saveMessages(stored);
}

const MISSING_MESSAGING_CONTEXT_MESSAGE = 'Local messaging identity is unavailable';

function getDefaultLocalIdentityIdHint(): string | undefined {
  const connectionIdentityId = (wsClient.getConnectionContext()?.identityId || '').toString().trim();
  if (connectionIdentityId) return connectionIdentityId;
  return undefined;
}

async function resolveLocalMessagingContext(localIdentityId?: string): Promise<MessagingLocalContext> {
  const identityHint = localIdentityId || getDefaultLocalIdentityIdHint();
  if (!identityHint) {
    throw new Error(MISSING_MESSAGING_CONTEXT_MESSAGE);
  }
  return resolveStrictMessagingLocalContext({
    localIdentityId: identityHint,
    timeoutMs: CONTEXT_TIMEOUT_MS
  });
}

async function signMessagingPayload(payload: object, localIdentityId?: string): Promise<{
  context: MessagingLocalContext;
  signature: string;
}> {
  const context = await resolveLocalMessagingContext(localIdentityId);
  const signature = await signPayloadWithKey(payload, context.signingKey);
  return { context, signature };
}

function buildSyncAckData(
  payload: { syncedThrough: number },
  context: MessagingLocalContext,
  signature: string
): WSMessageSyncAckData {
  return {
    type: 'msg:sync-ack',
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

function buildSyncStatusAckData(
  payload: { syncedThrough: number },
  context: MessagingLocalContext,
  signature: string
): WSMessageSyncStatusAckData {
  return {
    type: 'msg:sync-status-ack',
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

function buildStatusData(
  payload: { serverMessageId: string; status: 'delivered' | 'read' },
  context: MessagingLocalContext,
  signature: string
): WSMessageStatusData {
  return {
    type: 'msg:status',
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

function buildEditData(
  payload: { serverMessageId: string; ciphertext: string; senderCiphertext?: string; epoch?: number },
  context: { localDeviceId: string },
  signature: string
): WSMessageEditData {
  return {
    type: 'msg:edit',
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

function buildDeleteData(
  payload: { serverMessageId: string },
  context: { localDeviceId: string },
  signature: string
): WSMessageDeleteData {
  return {
    type: 'msg:delete',
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

function buildSyncRequestData(
  type: 'msg:sync' | 'msg:sync-status',
  payload: { since?: number },
  context: MessagingLocalContext,
  signature: string
) {
  return {
    type,
    payload,
    deviceId: context.localDeviceId,
    signature
  };
}

async function sendSyncAck(syncedThrough: number): Promise<void> {
  const payload = { syncedThrough };
  const { context, signature } = await signMessagingPayload(payload);
  const data = buildSyncAckData(payload, context, signature);
  wsClient.send({ type: 'message:sync-ack', data, timestamp: Date.now() });
}

async function sendSyncStatusAck(syncedThrough: number): Promise<void> {
  const payload = { syncedThrough };
  const { context, signature } = await signMessagingPayload(payload);
  const data = buildSyncStatusAckData(payload, context, signature);
  wsClient.send({ type: 'message:sync-status-ack', data, timestamp: Date.now() });
}

async function sendDelivered(serverMessageId: string): Promise<void> {
  const payload = { serverMessageId, status: 'delivered' as const };
  const { context, signature } = await signMessagingPayload(payload);
  if (isWsReadyForMessageContext(context)) {
    const data = buildStatusData(payload, context, signature);
    wsClient.send({ type: 'message:status', data, timestamp: Date.now() });
    return;
  }
  const response = await directMessagesApi.updateStatus(payload, context.localServerUrl, context.localIdentityId);
  if (response.result?.statusUpdate) {
    await applyMessageStatusUpdate(response.result.statusUpdate);
  }
}

async function sendMarkRead(peerIdentityId: string, readThrough: number, localIdentityId?: string): Promise<void> {
  const payload = { peerIdentityId, readThrough };
  const context = await resolveStrictHttpMessageActionContext({
    recipientIdentityId: peerIdentityId,
    localIdentityId: localIdentityId || getDefaultLocalIdentityIdHint() || ''
  });
  if (isWsReadyForMessageContext(context)) {
    const signature = await signPayloadWithKey(payload, context.signingKey);
    const data: WSMarkReadData = {
      type: 'msg:mark-read',
      payload,
      deviceId: context.localDeviceId,
      signature,
    };
    wsClient.send({ type: 'message:mark-read', data, timestamp: Date.now() });
    return;
  }
  const response = await directMessagesApi.markRead(payload, context.localServerUrl, context.localIdentityId);
  for (const update of response.result?.statusUpdates || []) {
    await applyMessageStatusUpdate(update);
  }
  if (response.result?.readCursor) {
    await applyMessageReadCursor(response.result.readCursor);
  }
}

function isWsReadyForMessageContext(context: MessageActionContext | MessagingLocalContext): boolean {
  const isTemporaryDevice = Object.values(useAuthStore.getState().temporaryMemberships)
    .some((membership) => membership.deviceId === context.localDeviceId);
  if (isTemporaryDevice) return false;
  const serverUrl = (context.localServerUrl || '').trim();
  if (!serverUrl || !wsClient.isConnectedToServer(serverUrl)) return false;
  const connection = wsClient.getConnectionContext();
  if (!connection) return false;
  return (connection.deviceId || '') === context.localDeviceId
    && (connection.identityId || '') === context.localIdentityId;
}

function warmUpWsForMessageContext(params: {
  recipientIdentityId: string;
  localIdentityId: string;
}) {
  void resolveStrictMessageActionContext({
    recipientIdentityId: params.recipientIdentityId,
    localIdentityId: params.localIdentityId,
    timeoutMs: 1_500
  }).catch(() => {});
}

async function resolvePreferredDirectMessageTransport(params: {
  recipientIdentityId: string;
  localIdentityId: string;
}): Promise<{ transport: 'ws' | 'http'; context: MessageActionContext }> {
  const context = await resolveStrictHttpMessageActionContext({
    recipientIdentityId: params.recipientIdentityId,
    localIdentityId: params.localIdentityId,
  });
  // Direct-message mutations are HTTP-first so the sender gets a reliable
  // request/response ACK even while WS is still reconnecting or re-registering.
  // We still warm WS in the background for live updates and subsequent sync.
  if (!isWsReadyForMessageContext(context)) {
    warmUpWsForMessageContext(params);
  }
  return { transport: 'http', context };
}

async function markPendingSendFailed(clientMessageId: string, error: unknown) {
  const pending = pendingOutbound.get(clientMessageId);
  if (pending?.timeoutId) {
    window.clearTimeout(pending.timeoutId);
  }
  pendingOutbound.delete(clientMessageId);
  await updateMessageStatus(clientMessageId, 'failed');
  showToast({
    message: hasCircleServerAccessRemoved(pending?.message.senderIdentityId)
      ? t('servers.serverAccessRemovedAction')
      : error instanceof Error ? error.message : t('common.unknownError'),
    variant: 'error'
  });
}

async function sendEncryptedMessageViaHttp(params: {
  actionContext: MessageActionContext;
  recipientIdentityId: string;
  ciphertext: string;
  senderCiphertext?: string;
  epoch?: number;
  senderSignature: string;
  clientMessageId: string;
  clientCreatedAt?: number;
  replyTo?: DirectMessageReplyReference | null;
}) {
  const response = await directMessagesApi.sendMessage({
    recipientIdentityId: params.recipientIdentityId,
    ciphertext: params.ciphertext,
    senderCiphertext: params.senderCiphertext,
    senderSignature: params.senderSignature,
    epoch: params.epoch,
    clientMessageId: params.clientMessageId,
    clientCreatedAt: params.clientCreatedAt,
    replyTo: params.replyTo,
  }, params.actionContext.localServerUrl, params.actionContext.localIdentityId);
  if (response.result?.ack) {
    await handleMessageAck(response.result.ack);
  }
  if (response.result?.statusUpdate) {
    await applyMessageStatusUpdate(response.result.statusUpdate);
  }
}

async function editEncryptedMessageViaHttp(params: {
  actionContext: MessageActionContext;
  serverMessageId: string;
  ciphertext: string;
  senderCiphertext?: string;
  epoch?: number;
  senderSignature: string;
}) {
  const response = await directMessagesApi.editMessage({
    serverMessageId: params.serverMessageId,
    ciphertext: params.ciphertext,
    senderCiphertext: params.senderCiphertext,
    senderSignature: params.senderSignature,
    epoch: params.epoch,
  }, params.actionContext.localServerUrl, params.actionContext.localIdentityId);
  if (response.result?.mutation) {
    await applyMessageMutationUpdate(response.result.mutation);
  }
}

async function deleteEncryptedMessageViaHttp(params: {
  actionContext: MessageActionContext;
  serverMessageId: string;
}) {
  const response = await directMessagesApi.deleteMessage({
    serverMessageId: params.serverMessageId,
  }, params.actionContext.localServerUrl, params.actionContext.localIdentityId);
  if (response.result?.mutation) {
    await applyMessageMutationUpdate(response.result.mutation);
  }
}

async function requestMessageSync(since?: number): Promise<void> {
  const context = await resolveLocalMessagingContext();
  const payload = typeof since === 'number' && Number.isFinite(since) ? { since } : {};
  if (!isWsReadyForMessageContext(context)) {
    const response = await directMessagesApi.listMessages(payload, context.localServerUrl, context.localIdentityId);
    const data = response.result;
    if (!data) return;
    const stored = toStoredMessages(data.messages, context.localIdentityId);
    await saveMessages(stored);
    if (!data.hasMore && data.readCursors?.length) {
      for (const cursor of data.readCursors) {
        await markThreadReadUpTo(context.localIdentityId, cursor.peerIdentityId, cursor.readThrough);
      }
    }
    await finalizePagedSync(data, {
      acknowledge: async () => {},
      continueFrom: requestMessageSync
    });
    return;
  }
  const signature = await signPayloadWithKey(payload, context.signingKey);
  const syncData = buildSyncRequestData('msg:sync', payload, context, signature);
  wsClient.send({
    type: 'message:sync',
    data: syncData,
    timestamp: Date.now()
  });
}

async function requestMessageStatusSync(since?: number): Promise<void> {
  const context = await resolveLocalMessagingContext();
  const payload = typeof since === 'number' && Number.isFinite(since) ? { since } : {};
  if (!isWsReadyForMessageContext(context)) {
    const response = await directMessagesApi.listStatusUpdates(payload, context.localServerUrl, context.localIdentityId);
    const data = response.result;
    if (!data) return;
    for (const update of data.updates) {
      await applyMessageStatusUpdate(update);
    }
    await finalizePagedSync(data, {
      acknowledge: async () => {},
      continueFrom: requestMessageStatusSync
    });
    return;
  }
  const signature = await signPayloadWithKey(payload, context.signingKey);
  const syncStatusData = buildSyncRequestData('msg:sync-status', payload, context, signature);
  wsClient.send({
    type: 'message:sync-status',
    data: syncStatusData,
    timestamp: Date.now()
  });
}

async function requestSync(): Promise<void> {
  await requestMessageSync();
  await requestMessageStatusSync();
}

async function syncDirectMessagesViaHttp(params: {
  localIdentityId: string;
  peerIdentityId: string;
  since?: number;
}): Promise<void> {
  const context = await resolveStrictHttpMessageActionContext({
    recipientIdentityId: params.peerIdentityId,
    localIdentityId: params.localIdentityId
  });
  const payload = typeof params.since === 'number' && Number.isFinite(params.since)
    ? { since: params.since, limit: FOCUSED_SYNC_PAGE_SIZE, peerIdentityId: params.peerIdentityId }
    : { limit: FOCUSED_SYNC_PAGE_SIZE, peerIdentityId: params.peerIdentityId };
  const response = await directMessagesApi.listMessages(payload, context.localServerUrl, context.localIdentityId);
  const data = response.result;
  if (!data) return;

  const stored = toStoredMessages(data.messages, context.localIdentityId);
  await saveMessages(stored);
  if (!data.hasMore && data.readCursors?.length) {
    for (const cursor of data.readCursors) {
      await markThreadReadUpTo(context.localIdentityId, cursor.peerIdentityId, cursor.readThrough);
    }
  }

  if (data.hasMore && Number.isFinite(data.syncedThrough) && data.syncedThrough !== params.since) {
    await syncDirectMessagesViaHttp({
      localIdentityId: params.localIdentityId,
      peerIdentityId: params.peerIdentityId,
      since: data.syncedThrough
    });
  }
}

async function syncDirectStatusesViaHttp(params: {
  localIdentityId: string;
  peerIdentityId: string;
  since?: number;
}): Promise<void> {
  const context = await resolveStrictHttpMessageActionContext({
    recipientIdentityId: params.peerIdentityId,
    localIdentityId: params.localIdentityId
  });
  const payload = typeof params.since === 'number' && Number.isFinite(params.since)
    ? { since: params.since, limit: FOCUSED_SYNC_PAGE_SIZE, peerIdentityId: params.peerIdentityId }
    : { limit: FOCUSED_SYNC_PAGE_SIZE, peerIdentityId: params.peerIdentityId };
  const response = await directMessagesApi.listStatusUpdates(payload, context.localServerUrl, context.localIdentityId);
  const data = response.result;
  if (!data) return;

  for (const update of data.updates) {
    await applyMessageStatusUpdate(update);
  }

  if (data.hasMore && Number.isFinite(data.syncedThrough) && data.syncedThrough !== params.since) {
    await syncDirectStatusesViaHttp({
      localIdentityId: params.localIdentityId,
      peerIdentityId: params.peerIdentityId,
      since: data.syncedThrough
    });
  }
}

export function requestDirectThreadSync(params: {
  localIdentityId: string;
  peerIdentityId: string;
}): Promise<void> {
  const localIdentityId = (params.localIdentityId || '').trim();
  const peerIdentityId = (params.peerIdentityId || '').trim();
  if (!localIdentityId || !peerIdentityId) return Promise.resolve();

  lastFocusedThread = { localIdentityId, peerIdentityId };

  const key = `${localIdentityId}:${peerIdentityId}`;
  const existing = focusedDirectThreadSyncInFlight.get(key);
  if (existing) return existing;

  const promise = (async () => {
    // Cursor is now the gapless, monotonic per-chat sequence (chat_seq), not a
    // timestamp. Strict `>` on a unique seq means no ties / no overshoot.
    const since = await getThreadMaxChatSeq(buildThreadKey(localIdentityId, peerIdentityId));

    await syncDirectMessagesViaHttp({ localIdentityId, peerIdentityId, since });
    await syncDirectStatusesViaHttp({ localIdentityId, peerIdentityId });
  })().finally(() => {
    focusedDirectThreadSyncInFlight.delete(key);
  });
  focusedDirectThreadSyncInFlight.set(key, promise);
  return promise;
}

// Fix 4b: run the global backstop sync plus a re-sync of the last opened thread,
// independent of any live event. Coalesced per-thread via focusedDirectThreadSyncInFlight.
function runUnconditionalSync(): void {
  if (wsClient.isExternalMode()) return;
  void requestSync().catch(() => {});
  if (lastFocusedThread) {
    void requestDirectThreadSync(lastFocusedThread).catch(() => {});
  }
}

export function initializeMessaging() {
  if (initialized) return;
  initialized = true;

  wsClient.on('registered', () => {
    if (wsClient.isExternalMode()) return;
    // Jitter the reconnect-triggered sync to avoid a thundering herd.
    const delay = Math.floor(Math.random() * RECONNECT_SYNC_MAX_JITTER_MS);
    window.setTimeout(runUnconditionalSync, delay);
  });

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') runUnconditionalSync();
    });
  }

  if (!unconditionalSyncTimer && typeof window !== 'undefined') {
    unconditionalSyncTimer = setInterval(runUnconditionalSync, UNCONDITIONAL_SYNC_INTERVAL_MS);
  }

  wsClient.on('message:sync-result', async (message) => {
    const data = message.data as WSMessageSyncResultData;
    const recipientIdentityId = (data.messages.find((item) => {
      const candidate = (item.recipientIdentityId || '').trim();
      return !!candidate && !!useAuthStore.getState().identities[candidate];
    })?.recipientIdentityId || '').trim();
    const senderIdentityId = (data.messages.find((item) => {
      const candidate = (item.senderIdentityId || '').trim();
      return !!candidate && !!useAuthStore.getState().identities[candidate];
    })?.senderIdentityId || '').trim();
    const context = await resolveLocalMessagingContext(recipientIdentityId || senderIdentityId || undefined);
    const stored = toStoredMessages(data.messages, context.localIdentityId);
    await saveMessages(stored);
    if (!data.hasMore && data.readCursors?.length) {
      for (const cursor of data.readCursors) {
        await markThreadReadUpTo(context.localIdentityId, cursor.peerIdentityId, cursor.readThrough);
      }
    }
    await finalizePagedSync(data, {
      acknowledge: sendSyncAck,
      continueFrom: requestMessageSync
    });
  });

  wsClient.on('message:sync-status-result', async (message) => {
    const data = message.data as WSMessageSyncStatusResultData;
    for (const update of data.updates) {
      await applyMessageStatusUpdate(update);
    }
    await finalizePagedSync(data, {
      acknowledge: sendSyncStatusAck,
      continueFrom: requestMessageStatusSync
    });
  });

  wsClient.on('message:ack', async (message) => {
    const data = message.data as WSMessageAckData;
    await handleMessageAck(data);
  });

  wsClient.on('message:deliver', async (message) => {
    const data = message.data as WSMessageDeliverData;
    const localIdentityIdHint = resolveLocalIdentityHintForDeliveredMessage(data);
    const context = await resolveLocalMessagingContext(localIdentityIdHint);

    const isInbound = data.senderIdentityId !== context.localIdentityId;
    const isDeletedMutation = typeof data.deletedAt === 'number' && Number.isFinite(data.deletedAt);
    if (isInbound && !isDeletedMutation) {
      const verification = await verifyInboundMessage(data, context);
      if (!verification.isValid) {
        showToast({
          message: t('messages.errors.tamperedDropped', { name: verification.senderName }),
          variant: 'error',
          durationMs: 10000
        });
        await sendDelivered(data.serverMessageId);
        return;
      }

      markServerKeySeenIfNeeded(
        verification.contactId,
        verification.contactServerId,
        data.senderIdentityId
      );
    }

    const stored = toStoredMessages([data], context.localIdentityId);
    await saveMessages(stored);
    if (isInbound && !isDeletedMutation) {
      void refreshPresenceForIdentity(data.senderIdentityId, {
        localIdentityId: context.localIdentityId
      }).catch(() => {});
    }

    // In-app toast (only when the app is open/visible).
    // We intentionally do NOT toast for sync results to avoid spam.
    const isVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
    if (isVisible && isInbound && !isDeletedMutation) {
        const senderId = (data.senderIdentityId || '').trim();
        if (senderId) {
        const contact =
          useVaultStore.getState().findContactByIdentityId(senderId)
          || findTemporaryContactByIdentityId(useAuthStore.getState().temporaryMemberships, senderId);
        const contactName = (contact?.displayName || '').trim();
        const senderName = contactName || (data.senderIdentityName || '').trim() || senderId;

        showToast({
          message: t('messages.toastNewMessage', { name: senderName }),
          variant: 'info',
          durationMs: 5000
        });
      }
    }

    if (!isDeletedMutation) {
      await sendDelivered(data.serverMessageId);
    }
  });

  wsClient.on('message:mutation-update', async (message) => {
    const data = message.data as WSMessageMutationUpdateData;
    await applyMessageMutationUpdate(data);
  });

  wsClient.on('message:status-update', async (message) => {
    const data = message.data as WSMessageStatusUpdateData;
    await applyMessageStatusUpdate(data);
  });

  wsClient.on('message:read-cursor', async (message) => {
    const data = message.data as WSReadCursorPushData;
    await applyMessageReadCursor(data);
  });

  wsClient.on('error', (message) => {
    const code = (message.data as { code?: string })?.code;
    if (code === 'MESSAGE_TOO_LARGE') {
      showToast({
        message: t('common.messageTooLarge'),
        variant: 'error'
      });
    }
  });

  wsClient.on('group:chat-updated', async (message) => {
    const data = message.data as WSGroupChatUpdatedData;
    if (!data?.chatId) return;
    window.dispatchEvent(new Event('group-chats:updated'));
  });

  wsClient.on('group:message:deliver', async (message) => {
    const data = message.data as WSGroupMessageDeliverData;
    if (!data?.chatId) return;
    window.dispatchEvent(new Event('group-chats:updated'));
  });

  wsClient.on('group:message:updated', async (message) => {
    const data = message.data as WSGroupMessageUpdatedData;
    if (!data?.chatId) return;
    window.dispatchEvent(new Event('group-chats:updated'));
  });
}

function canSendMessage(ciphertext: string, senderCiphertext?: string): boolean {
  if (new TextEncoder().encode(ciphertext).length > MAX_MESSAGE_BYTES) return false;
  if (senderCiphertext && new TextEncoder().encode(senderCiphertext).length > MAX_MESSAGE_BYTES) return false;
  return true;
}

export async function markMessagesRead(
  serverMessageIds: string[],
  peerIdentityId: string,
  readThrough: number,
  localIdentityId?: string
): Promise<void> {
  for (const id of serverMessageIds) {
    await updateMessageStatus(id, 'read');
  }
  await sendMarkRead(peerIdentityId, readThrough, localIdentityId);
}

function scheduleAckTimeout(clientMessageId: string) {
  const pending = pendingOutbound.get(clientMessageId);
  if (!pending) return;
  if (pending.timeoutId) {
    window.clearTimeout(pending.timeoutId);
  }
  pending.timeoutId = window.setTimeout(() => {
    void updateMessageStatus(clientMessageId, 'failed');
    pending.rejectAck?.(new Error(t('shareTarget.errors.sendFailed')));
  }, ACK_TIMEOUT_MS);
  pendingOutbound.set(clientMessageId, pending);
}

function sendMessagePayload(params: {
  deviceId: string;
  recipientIdentityId: string;
  ciphertext: string;
  senderCiphertext?: string;
  epoch?: number;
  clientMessageId: string;
  clientCreatedAt?: number;
  replyTo?: DirectMessageReplyReference | null;
  signature: string;
}) {
  wsClient.send({
    type: 'message:send',
    data: {
      type: 'msg:send',
      payload: {
        recipientIdentityId: params.recipientIdentityId,
        ciphertext: params.ciphertext,
        senderCiphertext: params.senderCiphertext,
        epoch: params.epoch,
        clientMessageId: params.clientMessageId,
        clientCreatedAt: params.clientCreatedAt,
        replyTo: params.replyTo || undefined
      },
      deviceId: params.deviceId,
      signature: params.signature
    },
    timestamp: Date.now()
  });
}

export async function sendEncryptedMessage(params: {
  recipientIdentityId: string;
  localIdentityId?: string;
  plaintext: string;
  ciphertext: string;
  senderCiphertext?: string;
  epoch?: number;
  localHistoryCiphertext?: LocalHistoryCiphertext | null;
  clientMessageId: string;
  clientCreatedAt?: number;
  replyTo?: DirectMessageReplyReference | null;
  afterAck?: (ack: WSMessageAckData) => Promise<void> | void;
  waitForAck?: boolean;
}): Promise<void> {
  if (!canSendMessage(params.ciphertext, params.senderCiphertext)) {
    showToast({
      message: t('common.messageTooLarge'),
      variant: 'error'
    });
    return;
  }

  let actionContext: MessageActionContext;
  let transport: 'ws' | 'http';
  try {
    const preferred = await resolvePreferredDirectMessageTransport({
      recipientIdentityId: params.recipientIdentityId,
      localIdentityId: params.localIdentityId || getDefaultLocalIdentityIdHint() || '',
    });
    actionContext = preferred.context;
    transport = preferred.transport;
    if (hasCircleServerAccessRemoved(actionContext.localIdentityId)) {
      showToast({
        message: t('servers.serverAccessRemovedAction'),
        variant: 'warning'
      });
      return;
    }
  } catch (error) {
    console.error('Message send context setup failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageTransportError(error),
      variant: 'error'
    });
    return;
  }

  let signature: string;
  try {
    signature = await signPlaintextWithKey(
      params.plaintext,
      actionContext.signingKey
    );
  } catch (error) {
    console.error('Message signing failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: t('messages.errors.signFailed'),
      variant: 'error'
    });
    return;
  }

  const outboundMessage: StoredMessage = {
    serverMessageId: params.clientMessageId,
    clientMessageId: params.clientMessageId,
    senderIdentityId: actionContext.localIdentityId,
    recipientIdentityId: params.recipientIdentityId,
    ciphertext: params.ciphertext,
    senderCiphertext: params.senderCiphertext,
    senderSignature: signature,
    epoch: params.epoch,
    serverTimestamp: params.clientCreatedAt ?? Date.now(),
    replyTo: params.replyTo || null,
    status: 'sending',
    direction: 'outbound',
    localHistoryCiphertext: params.localHistoryCiphertext || null,
  };
  let ackPromise: Promise<void> | null = null;
  let resolveAck: (() => void) | undefined;
  let rejectAck: ((error: unknown) => void) | undefined;
  if (params.waitForAck) {
    ackPromise = new Promise<void>((resolve, reject) => {
      resolveAck = resolve;
      rejectAck = reject;
    });
  }
  pendingOutbound.set(params.clientMessageId, {
    message: outboundMessage,
    afterAck: params.afterAck,
    resolveAck,
    rejectAck
  });
  scheduleAckTimeout(params.clientMessageId);
  await saveMessages([outboundMessage]);
  try {
    if (transport === 'ws') {
      sendMessagePayload({
        deviceId: actionContext.localDeviceId,
        recipientIdentityId: params.recipientIdentityId,
        ciphertext: params.ciphertext,
        senderCiphertext: params.senderCiphertext,
        epoch: params.epoch,
        clientMessageId: params.clientMessageId,
        clientCreatedAt: params.clientCreatedAt,
        replyTo: params.replyTo || null,
        signature
      });
      if (ackPromise) await ackPromise;
      return;
    }

    await sendEncryptedMessageViaHttp({
      actionContext,
      recipientIdentityId: params.recipientIdentityId,
      ciphertext: params.ciphertext,
      senderCiphertext: params.senderCiphertext,
      epoch: params.epoch,
      senderSignature: signature,
      clientMessageId: params.clientMessageId,
      clientCreatedAt: params.clientCreatedAt,
      replyTo: params.replyTo || null,
    });
    if (ackPromise) await ackPromise;
  } catch (error) {
    console.error('Message send transport failed:', {
      code: getErrorCode(error),
      error
    });
    await markPendingSendFailed(params.clientMessageId, error);
    if (ackPromise) throw error;
  }
}

export async function retryEncryptedMessage(message: StoredMessage): Promise<void> {
  const clientMessageId = message.clientMessageId || message.serverMessageId;
  if (!message.senderSignature) {
    showToast({
      message: t('messages.errors.retryFailed'),
      variant: 'error'
    });
    return;
  }
  const now = Date.now();
  if (now - message.serverTimestamp > RETRY_WINDOW_MS) {
    showToast({
      message: t('messages.errors.retryExpired'),
      variant: 'error'
    });
    return;
  }
  await updateMessageStatus(message.serverMessageId, 'sending');
  pendingOutbound.set(clientMessageId, { message: { ...message, status: 'sending' } });
  scheduleAckTimeout(clientMessageId);
  try {
    const preferred = await resolvePreferredDirectMessageTransport({
      recipientIdentityId: message.recipientIdentityId,
      localIdentityId: message.senderIdentityId,
    });
    if (preferred.transport === 'ws') {
      sendMessagePayload({
        deviceId: preferred.context.localDeviceId,
        recipientIdentityId: message.recipientIdentityId,
        ciphertext: message.ciphertext,
        senderCiphertext: message.senderCiphertext,
        epoch: message.epoch ?? undefined,
        clientMessageId,
        clientCreatedAt: message.serverTimestamp,
        replyTo: message.replyTo || null,
        signature: message.senderSignature
      });
      return;
    }
    await sendEncryptedMessageViaHttp({
      actionContext: preferred.context,
      recipientIdentityId: message.recipientIdentityId,
      ciphertext: message.ciphertext,
      senderCiphertext: message.senderCiphertext,
      epoch: message.epoch ?? undefined,
      senderSignature: message.senderSignature,
      clientMessageId,
      clientCreatedAt: message.serverTimestamp,
      replyTo: message.replyTo || null,
    });
  } catch (error) {
    console.error('Retry message failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageRetryError(error),
      variant: 'error'
    });
  }
}

export async function editEncryptedMessage(params: {
  serverMessageId: string;
  recipientIdentityId: string;
  localIdentityId: string;
  plaintext: string;
  ciphertext: string;
  senderCiphertext?: string;
  epoch?: number;
}): Promise<void> {
  if (!canSendMessage(params.ciphertext, params.senderCiphertext)) {
    showToast({
      message: t('common.messageTooLarge'),
      variant: 'error'
    });
    return;
  }

  let actionContext: MessageActionContext;
  let transport: 'ws' | 'http';
  try {
    const preferred = await resolvePreferredDirectMessageTransport({
      recipientIdentityId: params.recipientIdentityId,
      localIdentityId: params.localIdentityId,
    });
    actionContext = preferred.context;
    transport = preferred.transport;
  } catch (error) {
    console.error('Edit message context setup failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageTransportError(error),
      variant: 'error'
    });
    return;
  }

  let signature: string;
  try {
    signature = await signPlaintextWithKey(params.plaintext, actionContext.signingKey);
  } catch (error) {
    console.error('Edit message signing failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: t('messages.errors.signFailed'),
      variant: 'error'
    });
    return;
  }

  try {
    if (transport === 'ws') {
      const data = buildEditData({
        serverMessageId: params.serverMessageId,
        ciphertext: params.ciphertext,
        senderCiphertext: params.senderCiphertext,
        epoch: params.epoch
      }, actionContext, signature);
      wsClient.send({ type: 'message:edit', data, timestamp: Date.now() });
      return;
    }
    await editEncryptedMessageViaHttp({
      actionContext,
      serverMessageId: params.serverMessageId,
      ciphertext: params.ciphertext,
      senderCiphertext: params.senderCiphertext,
      senderSignature: signature,
      epoch: params.epoch,
    });
  } catch (error) {
    console.error('Edit message transport failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageMutationError(error, 'edit'),
      variant: 'error'
    });
  }
}

export async function deleteEncryptedMessage(params: {
  serverMessageId: string;
  recipientIdentityId: string;
  localIdentityId: string;
}): Promise<void> {
  let actionContext: MessageActionContext;
  let transport: 'ws' | 'http';
  try {
    const preferred = await resolvePreferredDirectMessageTransport({
      recipientIdentityId: params.recipientIdentityId,
      localIdentityId: params.localIdentityId,
    });
    actionContext = preferred.context;
    transport = preferred.transport;
  } catch (error) {
    console.error('Delete message context setup failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageTransportError(error),
      variant: 'error'
    });
    return;
  }

  const payload = { serverMessageId: params.serverMessageId };
  let signature: string;
  try {
    signature = await signPayloadWithKey(payload, actionContext.signingKey);
  } catch (error) {
    console.error('Delete message signing failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: t('messages.errors.signFailed'),
      variant: 'error'
    });
    return;
  }

  try {
    if (transport === 'ws') {
      const data = buildDeleteData(payload, actionContext, signature);
      wsClient.send({ type: 'message:delete', data, timestamp: Date.now() });
      return;
    }
    await deleteEncryptedMessageViaHttp({
      actionContext,
      serverMessageId: params.serverMessageId,
    });
  } catch (error) {
    console.error('Delete message transport failed:', {
      code: getErrorCode(error),
      error
    });
    showToast({
      message: humanizeMessageMutationError(error, 'delete'),
      variant: 'error'
    });
  }
}
